import { useMemo } from 'react';
import {
  ReusableComponentsVehicleModeEnum,
  RouteTypeOfLineEnum,
} from '../../../../generated/graphql';
import { useUrlQuery } from '../../../../hooks';
import { Priority } from '../../../../types/enums';
import { AllOptionEnum } from '../../../../utils';
import { today } from '../../../../utils/time';
import { DisplayedSearchResultType, SearchConditions } from './Types';

export type FilterConditions = {
  readonly displayedType: DisplayedSearchResultType;
};

export type SearchParameters = {
  readonly search: SearchConditions;
  readonly filter: FilterConditions;
};

export enum SearchQueryParameterNames {
  Query = 'query',
  Priorities = 'priorities',
  TransportMode = 'transportMode',
  DisplayedType = 'displayedType',
  TypeOfLine = 'typeOfLine',
  ObservationDate = 'observationDate',
}

const DEFAULT_PRIORITIES = [Priority.Standard];
const DEFAULT_TRANSPORT_MODE = [AllOptionEnum.All];
const DEFAULT_TYPE_OF_LINE = AllOptionEnum.All;
const DEFAULT_DISPLAYED_DATA = DisplayedSearchResultType.Lines;
const DEFAULT_LABEL = '';
const DEFAULT_OBSERVATION_DATE = today();

export function useSearchQueryParser(): SearchParameters {
  const {
    getStringParamFromUrlQuery,
    getPriorityArrayFromUrlQuery,
    getTransportModeArrayFromUrlQuery,
    getEnumFromUrlQuery,
    getDateTimeFromUrlQuery,
  } = useUrlQuery();
  const query =
    getStringParamFromUrlQuery(SearchQueryParameterNames.Query) ??
    DEFAULT_LABEL;

  const priorities =
    getPriorityArrayFromUrlQuery(SearchQueryParameterNames.Priorities) ??
    DEFAULT_PRIORITIES;

  const transportMode: Array<
    ReusableComponentsVehicleModeEnum | AllOptionEnum
  > =
    getTransportModeArrayFromUrlQuery(
      SearchQueryParameterNames.TransportMode,
    ) ?? DEFAULT_TRANSPORT_MODE;

  const typeOfLine =
    getEnumFromUrlQuery(
      SearchQueryParameterNames.TypeOfLine,
      RouteTypeOfLineEnum,
    ) ?? DEFAULT_TYPE_OF_LINE;

  const displayedType =
    getEnumFromUrlQuery(
      SearchQueryParameterNames.DisplayedType,
      DisplayedSearchResultType,
    ) ?? DEFAULT_DISPLAYED_DATA;
  // Apollo compares Temporal query variables by identity, so keep the date stable.
  const observationDate = useMemo(
    () =>
      getDateTimeFromUrlQuery(SearchQueryParameterNames.ObservationDate) ??
      DEFAULT_OBSERVATION_DATE,
    [getDateTimeFromUrlQuery],
  );

  return {
    search: {
      query,
      priorities,
      transportMode,
      typeOfLine,
      observationDate,
    },
    filter: {
      displayedType,
    },
  };
}
